import { Box, Stack, Typography } from '@mui/material';
import { useSuspenseQuery } from '@tanstack/react-query';
import { Suspense, useMemo, useState } from 'react';

import { _getAdminViewsUserStats } from '@/api/admin/queries';
import { ChartDataPoint } from '@/api/admin/types';
import { Typography as DsTypography } from '@/design-system';
import { useIsMobile } from '@/global-hooks/use-is-mobile';

import { ChartCard } from './chart-card';
import { ChartCardSkeleton } from './chart-card-skeleton';
import { analyticsData, CardProps, UserCardId, userChartCards } from './common';
import { TimeWrapper } from './styled';

export const makeDummyEmptyData = (days = 365): ChartDataPoint[] =>
  Array.from({ length: days }).map((_, i) => {
    const date = new Date();
    date.setDate(date.getDate() - (days - 1 - i));

    return {
      date: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      count: 0,
    };
  });

const timeRanges = ['1W', '1M', '1Y', 'All'] as const;
type TimeRange = (typeof timeRanges)[number];

const UsersChartsGrid = ({
  selectedRange,
  isMobile,
}: {
  selectedRange: TimeRange;
  isMobile: boolean;
}) => {
  const { data: userStats } = useSuspenseQuery(_getAdminViewsUserStats());
  const is7Days = selectedRange === '1W';

  const cardsWithData = useMemo(() => {
    const enhancers: Partial<
      Record<UserCardId, (_c: CardProps & { id: UserCardId }) => CardProps>
    > = {
      newUsers: (c) => ({
        ...c,
        value: is7Days ? userStats.newUsersLast7Days : userStats.newUsersLast30Days,
        chartData: is7Days
          ? userStats.dailyRegistrationsLast7Days
          : userStats.dailyRegistrationsLast30Days,
      }),
      totalUsers: (c) => ({
        ...c,
        value: userStats.totalUsers,
        delta: is7Days
          ? `~${userStats.newUsersLast7Days} users/7 days`
          : `~${userStats.newUsersLast30Days} users/30 days`,
        chartData: is7Days
          ? userStats.dailyRegistrationsLast7DaysCumulative
          : userStats.dailyRegistrationsLast30DaysCumulative,
      }),
      newVerifiedUsers: (c) => ({
        ...c,
        value: is7Days ? userStats.verifiedUsersLast7Days : userStats.verifiedUsersLast30Days,
        chartData: is7Days
          ? userStats.dailyVerificationsLast7Days
          : userStats.dailyVerificationsLast30Days,
      }),
      totalVerifiedUsers: (c) => ({
        ...c,
        value: userStats.totalKycVerifiedUsers,
        delta: is7Days
          ? `~${userStats.kycVerifiedUsersLast7Days} users/7 days`
          : `~${userStats.kycVerifiedUsersLast30Days} users/30 days`,
        chartData: is7Days
          ? userStats.dailyKycVerificationsLast7DaysCumulative
          : userStats.dailyKycVerificationsLast30DaysCumulative,
      }),
    };

    const fallbackData = makeDummyEmptyData(is7Days ? 7 : 30);
    return userChartCards.map((c) => enhancers[c.id]?.(c) ?? { ...c, chartData: fallbackData });
  }, [userStats, is7Days]);

  return (
    <Box display="grid" gridTemplateColumns={isMobile ? '1fr' : 'repeat(2, 1fr)'} gap="26px">
      {cardsWithData.map((card, index) => (
        <ChartCard key={index} card={card} withYAxis />
      ))}
    </Box>
  );
};

export const AnalyticsTab = () => {
  const isMobile = useIsMobile();
  const [selectedRange, setSelectedRange] = useState<TimeRange>('1W');

  const tabs = useMemo(
    () => Array.from(new Set(analyticsData.map(({ title }) => title.split(' ')[0]))),
    [],
  );
  const [activeTab, setActiveTab] = useState<string>(tabs[0] ?? '');

  const filteredSection = useMemo(
    () => analyticsData.filter(({ title }) => title.startsWith(activeTab)),
    [activeTab],
  );

  return (
    <Stack flexDirection="column" mt="16px" gap="16px">
      <DsTypography variant="h5">Statistics</DsTypography>

      <Stack direction="row" gap="24px" mb={2}>
        {tabs.map((tab) => {
          const active = tab === activeTab;
          return (
            <Typography
              key={tab}
              fontWeight={600}
              sx={{
                pb: '8px',
                cursor: 'pointer',
                color: active ? '#FFFFFF' : '#9CA0A2',
                borderBottom: `2px solid ${active ? '#39CA88' : 'transparent'}`,
                transition: 'color .2s, border-color .2s',
              }}
              onClick={() => setActiveTab(tab)}
            >
              {tab} Statistics
            </Typography>
          );
        })}
      </Stack>

      <Stack gap="8px" justifyContent="flex-end" marginBottom="30px">
        {timeRanges.map((range) => {
          const active = selectedRange === range;
          return (
            <TimeWrapper
              key={range}
              onClick={() => setSelectedRange(range)}
              sx={{
                backgroundColor: active ? '#1B202B' : 'transparent',
                color: active ? '#FFFFFF' : '#9CA0A2',
                display: activeTab === 'Users' ? 'inline-flex' : 'none',
              }}
            >
              {range}
            </TimeWrapper>
          );
        })}
      </Stack>

      <Suspense
        fallback={
          <Box display="grid" gridTemplateColumns={isMobile ? '1fr' : 'repeat(2, 1fr)'} gap="26px">
            {Array.from({ length: 9 }).map((_, i) => (
              <ChartCardSkeleton key={i} />
            ))}
          </Box>
        }
      >
        {filteredSection.map(({ title, data }) => (
          <Box key={title}>
            {title === 'Users Statistics' ? (
              <UsersChartsGrid selectedRange={selectedRange} isMobile={isMobile} />
            ) : (
              <Box
                display="grid"
                gridTemplateColumns={isMobile ? '1fr' : 'repeat(2, 1fr)'}
                gap="26px"
              >
                {data.map((card, index) => (
                  <ChartCard key={index} card={card} withYAxis />
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Suspense>
    </Stack>
  );
};
